import { useClientTranslation } from '@lib/i18n/client';
import { TariffListing } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-listing';
import { GeneralView } from '@modules/get-slash-internet-card/components/dialog-views/general-view';
import { ViewStack } from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import { useCallback } from 'react';

import type { RefreshDialogHandler } from '../dialog-view-renderer';
import type { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type { ViewStackContext } from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import type { InternetCardViewData } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import type {
  DocumentRenderer,
  TextRenderer,
} from '@modules/get-slash-internet-card/lib/utils';
import type { Locale } from '@tuic/i18n';
import type { ComponentPropsWithoutRef, FC } from 'react';

type Props = {
  locale: Locale;
  getSlashCredentials: GetSlashCredentials;
  useDebugEndpoints?: boolean;
  data: InternetCardViewData['bookingView'];
  termsOfUseCheckbox?: ComponentPropsWithoutRef<
    typeof TariffListing
  >['termsOfUseCheckbox'];
  textRenderer: TextRenderer;
  documentRenderer: DocumentRenderer;
  refreshDialog: RefreshDialogHandler;
};

export const BookingView: FC<Props> = props => {
  const {
    locale,
    getSlashCredentials,
    useDebugEndpoints,
    termsOfUseCheckbox,
    data,
    textRenderer,
    documentRenderer,
    refreshDialog,
  } = props;

  const { t } = useClientTranslation();

  const showTariffList = useCallback(
    (push: ViewStackContext['push']) => {
      push(
        <TariffListing
          locale={locale}
          useDebugEndpoints={useDebugEndpoints}
          getSlashCredentials={getSlashCredentials}
          tariffs={data?.tariffs}
          fallbackImages={data?.fallbackImages}
          refreshDialog={refreshDialog}
          termsOfUseCheckbox={termsOfUseCheckbox}
        />,
      );
    },
    [
      getSlashCredentials,
      locale,
      useDebugEndpoints,
      data?.tariffs,
      data?.fallbackImages,
      refreshDialog,
    ],
  );

  return (
    <ViewStack>
      {({ push }) => (
        <GeneralView
          title={textRenderer(data?.headline)}
          text={documentRenderer(data?.text)}
          buttons={[
            {
              children:
                data?.buttonLabels?.discoverTariffs ?? t('Tarife entdecken'),
              variant: 'primary',
              onClick: () => showTariffList(push),
            },
          ]}
        />
      )}
    </ViewStack>
  );
};
